import React, { Component } from 'react';
import { IbssPage } from '../../../../../Components/Core/BasePage/IbssPage';
import { appContext } from '../../../../../AppContext';
import { RouteComponentProps, withRouter } from "react-router-dom";
import { Box, Grid, Typography } from '@mui/material';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import IbssActionButton, { IActionButton } from '../../../../../Components/Buttons/ActionButton/IbssActionButton';
import { Equipment, storeDetails } from './DataModels';
import { BaseQueryParams } from '../../../../../Components/Core/BasePage/BaseQueryParams';
import IbssSvgIcon from '../../../../../Components/Icons/SvgIcon/IbssSvgIcon';
import CreateIcon from '@mui/icons-material/Create';
import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ConfirmModal from '../../../../../Components/Dialogs/ConfirmDialog/ConfirmModal';
import IbssFilter from '../../../../../Components/Forms/Filter/IbssFilter';
import IbssSnackbar from '../../../../../Components/Snackbar/IbssSnackbar';
import Helper from '../../../../../Common/Helper';
import EditEquipmentDrawer from './EditEquipmentDrawer';
import Bin from '../../../../../Components/Icons/Bin';
import { PagedResponse } from '../../../../../Providers.Api/Models';
import ServerDataGrid, { DataGridQueryResult, IDataQuery, IPages } from '../../../../../Components/Data/ServerDataGrid/ServerDataGrid';
import { rights } from '../../../../../Common/RightsManager';
import IbssPageHeader from '../../../../../Components/Headings/PageHeading/IbssPageHeader';


class ListEquipments extends IbssPage<IProps, IState, QueryParams>
{
    private get labels() { return appContext().labels; }
    private get appState() { return appContext().state; }
    private dataGridRef = React.createRef<ServerDataGrid<IEquipment>>();
    private get dataGrid() { return this.dataGridRef.current; }
    private get localStorage() { return appContext().localStorageProvider; }
    private userCanUpdate: boolean;
    private userCanCreate: boolean;
    private userCanDelete: boolean;

    private columns: GridColDef<IEquipment>[];

    constructor(props: IProps)
    {
        super(props, new QueryParams());

        this.state =
        {
            buildingId: this.appState.buildingId,
            isLoading: false,
            openDrawer: false,
            isEditMode: false,
            showConfirmDialog: false,
            equipmentSavedSnackBar: false,
            filterText: '',
            storeList: [],
            images: [],
            selectedEquipment: [],
            pagesOfEquipment: {
                pages: [],
                pageIndex: 0,
                skipToken: '',
            },
        };

        this.columns = this.initializeDataGridColumns;
        this.userCanCreate = rights.DATAMODEL.Equipment.Create.has();
        this.userCanUpdate = rights.DATAMODEL.Equipment.Update.has();
        this.userCanDelete = rights.DATAMODEL.Equipment.Delete.has();
    }

    public async componentDidMount(): Promise<void>
    {
        this.pageTitle = this.labels.funcEquipment_S
    }

    public async queryParamsDidUpdate(): Promise<void>
    {
        const queryParams = this.queryParams
        await this.setStateAsync({ buildingId: queryParams.building ?? 1 });
        await this.dataGrid?.refresh();
        await this.loadStoreList();
        await this.getImages();
    }

    private async dataQueryChanged(gridQuery: IDataQuery): Promise<DataGridQueryResult<IEquipment>>    
    {
        this.setState({ isLoading: true });
        const equipmentData = await appContext().ibssApiClientV2.v2.byNodeid.equipment.get<PagedResponse<Equipment[]>>({
            nodeId: this.state.buildingId,
            select: Equipment,
            top: 100,
            skipToken: gridQuery.skipToken

        });

        const equipments: IEquipment[] = equipmentData.value.map(i => ({
            id: i.Equip_Id,
            equipmentNodeId: i.Node_Id,
            equipmentName: i.Equip_Name,
            equipmentType: i.Equip_Type,
            storeSpaceId: i.Store_Space_Id,
            equipmentStatusInfo: i.Equip_Status,
            equipmentClass: i.Equip_Class,
            isEquipmentEnabled: i.Equip_IsEnabled === 1 ? true : false,
            equipmentTagNo: i.Equip_Tag_No,
            equipmentSerialNo: i.Equip_Serial_No,
            equipmentCategory: i.Equip_Category,
            imageUrl: i.ImageURI,
        }));

        this.setState({ isLoading: false });
        return new DataGridQueryResult(equipments, equipmentData.skipToken);
    }

    private filterEquipments(searchTerm: string): IPages<IEquipment>
    {
        if (!searchTerm)
        {
            return this.state.pagesOfEquipment;
        }

        const lowerSearchTerm = searchTerm.toLowerCase();
        const equipment = this.state.pagesOfEquipment.pages.flatMap(x => x.rows)
            .filter(equipment =>
                equipment.equipmentName.toLowerCase().includes(lowerSearchTerm) ||
                equipment.equipmentType.toLowerCase().includes(lowerSearchTerm) ||
                equipment.storeSpaceId.toLowerCase().includes(lowerSearchTerm)
            );
        return { pages: [{ rows: equipment }], skipToken: '', pageIndex: 0 }
    }

    private async loadStoreList(): Promise<void> 
    {
        const storeList = await appContext().ibssApiClientV2.v2.byNodeid.spaces.get<PagedResponse<storeDetails[]>>({
            nodeId: this.state.buildingId,
            select: storeDetails,
            filter: `Space_Type eq 'EquipmentRoom'`,
        });

        const options = storeList.value
            .map(i => ({ label: i.Space_Name, value: i.Space_Id }))
            .sort((a, b) => (a.label < b.label ? - 1 : 1));
        this.setState({ storeList: options });
    }

    private async getImages(): Promise<void> 
    {
        try 
        {
            const EquipmentFiles = await appContext().webEntryApiClientV1.api.webentry.v1.file.byPath.get<IFile[]>({
                path: 'c/Equipment'
            });
            const EquipmentFilesWithPath = EquipmentFiles.map((item: IFile) =>
            {
                let newItem = { ...item }
                newItem.path = `https://storage.ibss.${Helper.environment}/images/c/Equipment/${item.name}`
                return newItem
            })
            this.setState({ images: EquipmentFilesWithPath });
        }
        catch (error) 
        {

        }
    }

    private async enableEquipment(): Promise<void> 
    {
        try 
        {
            this.setState({ isLoading: true });

            await Promise.all(
                this.state.selectedEquipment.map(equipment =>
                    appContext().ibssApiClientV2.v2.byNodeid.equipment.enable.patch({
                        nodeId: equipment.equipmentNodeId,
                        equipid: equipment.id,
                    })
                )
            );
            await this.dataGrid?.refresh();
            this.setState({ equipmentSavedSnackBar: true, selectedEquipment: [] });
        }
        finally
        {
            this.setState({ isLoading: false });
        }
    }

    private async disableEquipment(): Promise<void> 
    {
        try 
        {
            this.setState({ isLoading: true });

            await Promise.all(
                this.state.selectedEquipment.map(equipment =>
                    appContext().ibssApiClientV2.v2.byNodeid.equipment.disable.patch({
                        nodeId: equipment.equipmentNodeId,
                        equipid: equipment.id,
                    })
                )
            );
            await this.dataGrid?.refresh();
            this.setState({ equipmentSavedSnackBar: true, selectedEquipment: [] });
        }
        finally
        {
            this.setState({ isLoading: false });
        }
    }

    private async deleteEquipment(): Promise<void> 
    {
        this.setState({ isLoading: true });
        try 
        {
            this.setState({ isLoading: true });

            await Promise.all(
                this.state.selectedEquipment.map(equipment =>
                    appContext().ibssApiClientV2.v2.byNodeid.equipment.byEquipid.delete({
                        nodeId: equipment.equipmentNodeId,
                        equipid: equipment.id,
                    })
                )
            );
            await this.dataGrid?.refresh();
        }
        finally
        {
            this.setState({ isLoading: false, showConfirmDialog: false });
        }
    }

    private handleSelectionChange(selection: GridRowSelectionModel): void
    {
        const pageOfRows = this.dataGrid?.rows as IEquipment[];
        const selectedRows = pageOfRows.filter(row => selection.includes(row.id))
        this.setState({ selectedEquipment: selectedRows });
    }

    private handleButtonDisable(status: "Enable" | "Disable" | "Delete"): boolean
    {
        const { selectedEquipment } = this.state;

        if (!selectedEquipment.length)
        {
            return true;
        }

        const hasActive = selectedEquipment.some(({ isEquipmentEnabled }) => isEquipmentEnabled === true);
        const hasInactive = selectedEquipment.some(({ isEquipmentEnabled }) => isEquipmentEnabled === false);

        switch (status)
        {
            case "Enable":
                return hasActive || !hasInactive || !this.userCanUpdate;
            case "Disable":
                return hasInactive || !hasActive || !this.userCanUpdate;;
            case "Delete":
                return !this.userCanDelete;
            default:
                return true;
        }
    }

    private showConfirmModal(): void
    {
        this.setState({
            showConfirmDialog: true
        });
    }

    private handleUpdate(equipment: IEquipment): React.ReactNode
    {
        return (
            <IbssSvgIcon onClick={() => this.setState({ openDrawer: true, selectedEquipment: [equipment], isEditMode: true })}>
                <CreateIcon />
            </IbssSvgIcon>
        )
    }

    private closeDrawer(): void
    {
        this.setState({
            openDrawer: false,
            isEditMode: false,
            selectedEquipment: [],
        });
    }

    public get initializeDataGridColumns(): GridColDef<IEquipment>[]
    {
        const dataGridColumns: GridColDef<IEquipment>[] =
            [
                {
                    field: Helper.nameOf<IEquipment>('equipmentName'),
                    headerName: this.labels.funcEquipmentName_S,
                    width: 280,
                },
                {
                    field: Helper.nameOf<IEquipment>('id'),
                    headerName: this.labels.HubLabelId,
                    width: 280,
                },
                {
                    field: Helper.nameOf<IEquipment>('equipmentType'),
                    headerName: this.labels.funcType_S,
                    width: 280,
                },
                {
                    field: Helper.nameOf<IEquipment>('storeSpaceId'),
                    headerName: this.labels.funcStoreId_S,
                    width: 280,
                },
                {
                    field: Helper.nameOf<IEquipment>('isEquipmentEnabled'),
                    headerName: this.labels.HubLabelStatus,
                    width: 280,
                    renderCell: (params) => <>{params.row.isEquipmentEnabled ? this.labels.HubLabelActive : this.labels.HubLabelInActive}</>
                },
                {
                    field: '',
                    headerName: this.labels.HubLabelAction,
                    width: 280,
                    filterable: false,
                    sortable: false,
                    renderCell: (params) => <>{this.handleUpdate(params.row)}</>
                }
            ];

        return dataGridColumns;
    }

    public get actionButtons(): IActionButton[] 
    {
        const actionButtons: IActionButton[] =
            [
                {
                    label: this.labels.HubButtonDelete,
                    icon: (
                        <IbssSvgIcon fontSize='small' className="mr-1">
                            <Bin />
                        </IbssSvgIcon>
                    ),
                    color: "error",
                    onClick: () => this.showConfirmModal(),
                    disabled: this.handleButtonDisable("Delete"),
                },
                {
                    label: this.labels.HubLabelActive,
                    icon: (
                        <IbssSvgIcon fontSize='small' className="mr-1">
                            <CheckCircleIcon />
                        </IbssSvgIcon>
                    ),
                    color: "info",
                    onClick: () => this.enableEquipment(),
                    disabled: this.handleButtonDisable("Enable"),
                },
                {
                    label: this.labels.HubLabelInActive,
                    icon: (
                        <IbssSvgIcon fontSize='small' className="mr-1">
                            <CancelIcon />
                        </IbssSvgIcon>
                    ),
                    color: "error",
                    onClick: () => this.disableEquipment(),
                    disabled: this.handleButtonDisable("Disable"),
                },
                {
                    label: this.labels.HubButtonAdd,
                    className: "addIcon",
                    icon: (
                        <IbssSvgIcon>
                            <AddIcon />
                        </IbssSvgIcon>
                    ),
                    color: "primary",
                    disabled: !this.userCanCreate,
                    onClick: () => this.setState({ openDrawer: true }),
                },
            ];
        return actionButtons
    }

    public render(): JSX.Element
    {
        const filteredEquipment = this.filterEquipments(this.state.filterText);
        return (
            <div className="page-height-exct-header">
                <div className="rightPanel-main-content">
                    <div className="table-panel">
                        <Box>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                <IbssPageHeader
                                    title={this.labels.funcCreateManageEquipment_L}
                                    description={this.labels.funcEquipmentSubHeader_D}
                                />
                                </Grid>
                                <Grid item xs={12}>
                                    <IbssFilter
                                        searchTerm={this.state.filterText}
                                        onSearchTermChange={(event) => this.setState({ filterText: event.target.value })}
                                    />
                                    <Box component="div" sx={{ display: 'flex', justifyContent: 'right', alignItems: 'center', my: 1, mr: 0 }}>
                                        <IbssActionButton buttons={this.actionButtons} />
                                    </Box>
                                    {
                                        <Box>
                                            <ServerDataGrid
                                                ref={this.dataGridRef}
                                                loadDataOnMount={false}
                                                checkboxSelection
                                                columns={this.columns}
                                                disableRowSelectionOnClick
                                                pageSizeOptions={[100]}
                                                pages={filteredEquipment}
                                                onDataQueryChange={i => this.dataQueryChanged(i)}
                                                onPagesChange={pages => this.setStateAsync({ pagesOfEquipment: pages })}
                                                onRowSelectionModelChange={e => this.handleSelectionChange(e)}
                                            />
                                        </Box>
                                    }
                                </Grid>
                            </Grid>
                        </Box>
                        <ConfirmModal
                            show={this.state.showConfirmDialog}
                            modalHeading={this.labels.HubLabelModalConfirmTitle}
                            modalMessage={this.labels.funcAnnouncementsConfirmDelete_Message}
                            handleModal={() => this.setState({ showConfirmDialog: false })}
                            okButton={() => this.deleteEquipment()}
                        />
                        <EditEquipmentDrawer
                            open={this.state.openDrawer}
                            isEditMode={this.state.isEditMode}
                            buildingId={this.state.buildingId}
                            selectedEquipment={this.state.selectedEquipment[0]}
                            storeList={this.state.storeList}
                            images={this.state.images}
                            getImages={() => this.getImages()}
                            onClose={() => this.closeDrawer()}
                            loadEquipmentList={() => this.dataGrid?.refresh()}
                        />
                    </div>
                </div>
                <IbssSnackbar
                    open={this.state.equipmentSavedSnackBar}
                    autoHideDuration={4000}
                    onClose={() => this.setState({ equipmentSavedSnackBar: false })}
                    message={this.labels.funcEquipmentSnackbar_Message}
                    action={<CheckCircleIcon color='success' />}
                />
            </div>
        )
    }

}

export default withRouter(ListEquipments);

export class QueryParams extends BaseQueryParams
{
}

export interface IProps extends RouteComponentProps 
{
}

export interface IState 
{
    buildingId: number;
    isLoading: boolean;
    openDrawer: boolean;
    filterText: string;
    isEditMode: boolean
    selectedEquipment: IEquipment[];
    showConfirmDialog: boolean;
    equipmentSavedSnackBar: boolean;
    pagesOfEquipment: IPages<IEquipment>;
    storeList: Array<IListOption<string>>;
    images: IFile[];
}

export interface IEquipment
{
    id: string;
    equipmentName: string;
    equipmentType: string;
    storeSpaceId: string;
    equipmentNodeId: number;
    equipmentClass: string;
    isEquipmentEnabled: boolean;
    equipmentTagNo: string;
    equipmentSerialNo: string;
    equipmentStatusInfo: string;
    equipmentCategory: string;
    imageUrl: string;
}

export interface IListOption<TValue>
{
    label: string,
    value: TValue,
}

export interface IFile
{
    name: string;
    path: string;
}